import React, { useState } from 'react';
import { useQueryClient } from 'react-query';
import { Button } from 'modules/v2/common/AtomicDesign/atoms';
import { removeScheduledCancellation } from 'modules/api';
import { trackUsage } from 'modules/v2/common/utils/trackUsage';
import { formatNoticeDate, markReactivated } from 'modules/v2/utils/subscriptionNotice';

interface KeepMembershipModalProps {
  isOpen: boolean;
  // The end of the current term, when the membership would have been canceled
  // and now renews instead.
  renewsOn: string | null;
  onClose: () => void;
}

/**
 * One-click undo of a scheduled cancellation: confirms, removes the
 * cancellation through digital-services, refreshes the subscription and turns
 * the red cancellation notice into the green "active again" one.
 */
const KeepMembershipModal: React.FC<KeepMembershipModalProps> = ({ isOpen, renewsOn, onClose }) => {
  const queryClient = useQueryClient();
  const [isSaving, setIsSaving] = useState(false);
  const [hasError, setHasError] = useState(false);

  if (!isOpen) return null;

  const close = () => {
    if (isSaving) return;
    setHasError(false);
    onClose();
  };

  const keepMembership = async () => {
    setIsSaving(true);
    setHasError(false);
    try {
      await removeScheduledCancellation();
      trackUsage('plan', 'keep_membership');
      markReactivated(renewsOn);
      await queryClient.invalidateQueries('getCustomerSubscription');
      setIsSaving(false);
      onClose();
    } catch {
      setIsSaving(false);
      setHasError(true);
    }
  };

  return (
    <div
      role="presentation"
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1000] p-4"
      // Close on a click on the backdrop itself, not inside the dialog.
      onClick={(e) => e.target === e.currentTarget && close()}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="keep-membership-title"
        className="bg-white rounded-lg max-w-md w-full shadow-xl overflow-hidden"
      >
        <div className="h-2 bg-success-500" />
        <div className="p-6">
          <h3 id="keep-membership-title" className="text-xl font-bold text-neutral-800 mb-3">
            Keep your membership?
          </h3>
          <p className="text-neutral-700 leading-relaxed">
            Your membership will stay active
            {renewsOn ? (
              <>
                {' '}and renew on <span className="font-semibold">{formatNoticeDate(renewsOn)}</span>
              </>
            ) : null}
            . Nothing changes in your plan.
          </p>
          {hasError && (
            <p role="alert" className="text-sm text-error-600 mt-3">
              We couldn&apos;t reactivate your membership. Please try again or email
              support@authorify.com.
            </p>
          )}
          <div className="flex gap-3 justify-end mt-6">
            <button
              type="button"
              onClick={close}
              disabled={isSaving}
              className="px-4 py-2 text-sm text-gray-600 bg-gray-100 hover:bg-gray-200 rounded cursor-pointer transition-colors"
            >
              Not now
            </button>
            <Button type="primary" onClick={keepMembership} disabled={isSaving} isLoading={isSaving}>
              {isSaving ? 'Keeping your membership…' : 'Keep my membership'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default KeepMembershipModal;
